import { useState } from 'react';

import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GRAY, TEXT } from 'constants/colors';

import { displayFlooredNumber } from 'utils/misc';

import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';

import { FontAwesomeSvgIcon } from 'components/shared';

const SourceCardStyles = styled.div`
  border: 1px dashed #1e958c;
  padding: 30px 30px 20px;
  background-color: #00b1aa0f;
  border-radius: 3px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  .header {
    &:not(:last-child) {
      margin-bottom: 30px;
    }

    svg {
      margin-right: 20px;
    }

    p {
      margin-bottom: 0;
      flex-grow: 1;
    }

    .hours {
      position: relative;
      margin-left: 20px;

      .text {
        position: absolute;
        right: 0;
        top: 0;
        transform: translateY(-100%);
      }

      .total {
        font-size: 16px;
      }
    }
  }

  .MuiSlider-root {
    margin-left: 10px;
  }

  .MuiSlider-valueLabel {
    background-color: unset;
    color: ${TEXT};
    font-size: 16px;
    top: -4px;
  }

  .max {
    width: 50px;
    font-size: 16px;
    color: ${GRAY};
    margin-left: 10px;
  }

  .button-container {
    width: 110px;
  }
`;

type Props = {
  icon?: IconDefinition;
  description: string;
  maxHours: number;
  className?: string;
  readonly?: boolean;
  remainingHours: number;
  onChange?: (value: number) => void;
  onSave: (value: number) => void;
} & (
  | {
      type: 'slider' | 'export';
      sliderMax: number;
    }
  | {
      type: 'number';
    }
);

export default function SourceCard(props: Props) {
  const {
    type,
    icon,
    description,
    maxHours,
    className,
    readonly,
    remainingHours,
    onChange,
    onSave
  } = props;
  const { t } = useTranslation('common');
  const [value, setValue] = useState(0);

  const onSliderChange = (e, value: number | number[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    setValue(val);
    if (onChange) {
      onChange(val);
    }
  };

  const onInputChange = (valueStr: string) => {
    if (valueStr !== '' && !Number.isNaN(Number(valueStr))) {
      let value = Math.abs(parseFloat(parseFloat(valueStr).toFixed(2)));
      value = value < remainingHours ? value : remainingHours;
      setValue(value);
      if (onChange) {
        onChange(value);
      }
    } else {
      setValue(-1);
    }
  };
  const maxSliderHours = type === 'number' ? 0 : Math.max(0, props?.sliderMax);

  const isDisabledBtn =
    type === 'number'
      ? value > maxHours || value < 1
      : maxHours - value < 0 || !props.sliderMax;
  const step = maxSliderHours < 1 ? maxSliderHours : 1;

  return (
    <SourceCardStyles className={classNames('card', className)}>
      <div className="header d-flex v-center between">
        {icon && <FontAwesomeSvgIcon icon={icon} />}
        <p>{description}</p>
        {type === 'slider' && (
          <div className="hours">
            <div className="text">{t('hours')}</div>
            <div className="total semibold">
              {displayFlooredNumber(maxSliderHours - value)}
            </div>
          </div>
        )}
      </div>
      {!readonly && (
        <div className="d-flex v-center between">
          {type !== 'number' && (
            <>
              <Slider
                defaultValue={0}
                min={0}
                step={step}
                max={maxSliderHours}
                size="small"
                value={value}
                valueLabelDisplay="on"
                onChange={onSliderChange}
              />
              <div className="max semibold text-right">
                {displayFlooredNumber(maxSliderHours)}
              </div>
            </>
          )}
          {type === 'number' && (
            <div className="d-flex v-center">
              <TextField
                label={t('amount')}
                type="number"
                variant="outlined"
                value={value === -1 ? '' : value}
                style={{ width: 100, marginRight: 20 }}
                onChange={(e) => onInputChange(e.target.value)}
              />{' '}
              <span className="semibold">{t('hours')}</span>
            </div>
          )}
          <div className="button-container d-flex end">
            <Button
              variant="contained"
              size="small"
              className="deposit-btn"
              disabled={!value || (type !== 'export' && isDisabledBtn)}
              onClick={() => {
                onSave(value);
                setValue(0);
              }}
            >
              {type === 'export' ? t('balanceBudget.export') : t('deposit')}
            </Button>
          </div>
        </div>
      )}
    </SourceCardStyles>
  );
}
